import { makeAuthenticatedRequest } from "./api";

export const getSummaryDetails = async (classroomId, studentId = "") => {
  try {
    let URL = `/examAttempt/${classroomId}/performance`;
    if (studentId) {
      URL += `?userId=${studentId}`;
    }
    return await makeAuthenticatedRequest(URL, "GET")
      .then((response) => {
        if (!response.ok) {
          return { ok: false, error: response };
        }
        return response.json();
      })
      .then((data) => {
        return { ok: true, data: data };
      })
      .catch((error) => {
        console.error("Fetch error:", error);
        return { ok: false, error: error };
      });
  } catch (error) {
    console.error("Error during getting classrooms:", error);
    return { ok: false, error: error };
  }
};

export const getTopicsOfSubjects = async (subjectIds = []) => {
  try {
    let URL = `/topics/get_topics_of_subjects`;
    return await makeAuthenticatedRequest(
      URL,
      "POST",
      JSON.stringify({
        subjectIds,
      })
    )
      .then((response) => {
        if (!response.ok) {
          return { ok: false, error: response };
        }
        return response.json();
      })
      .then((data) => {
        return { ok: true, data: data };
      })
      .catch((error) => {
        console.error("Fetch error:", error);
        return { ok: false, error: error };
      });
  } catch (error) {
    console.error("Error during getting classrooms:", error);
    return { ok: false, error: error };
  }
};

export const getClassActivitesDetails = async ({
  classroomId = "",
  startDate = "",
  examType = "",
  page = 0,
  pageSize = 10,
}) => {
  try {
    const URL = `/examAttempt/classroom/${classroomId}?from=${startDate}&examType=${examType}&pageNumber=${page}&pageSize=${pageSize}&classroomId=${classroomId}`;
    return await makeAuthenticatedRequest(URL, "GET")
      .then((response) => {
        // Check if the request was successful (status code 200-299)
        if (!response.ok) {
          return { ok: false, error: response };
        }
        // Parse the response as JSON
        return response.json();
      })
      .then((data) => {
        // Handle the JSON data
        return { ok: true, data: data };
      })
      .catch((error) => {
        // Handle errors
        console.error("Fetch error:", error);
        return { ok: false, error: error };
      });
  } catch (error) {
    // Handle network errors or unexpected issues
    console.error("Error during getting classrooms:", error);
    return { ok: false, error: error };
  }
};

export const getIeltsClassActivitesDetails = async ({
  classroomId = "",
  startDate = "",
  examType = "",
  page = 0,
  pageSize = 10,
}) => {
  try {
    const URL = `/ielts_exam_attempts/classroom/${classroomId}?from=${startDate}&examType=${examType}&pageNumber=${page}&pageSize=${pageSize}&classroomId=${classroomId}`;
    return await makeAuthenticatedRequest(URL, "GET")
      .then((response) => {
        // Check if the request was successful (status code 200-299)
        if (!response.ok) {
          return { ok: false, error: response };
        }
        // Parse the response as JSON
        return response.json();
      })
      .then((data) => {
        // Handle the JSON data
        return { ok: true, data: data };
      })
      .catch((error) => {
        // Handle errors
        console.error("Fetch error:", error);
        return { ok: false, error: error };
      });
  } catch (error) {
    // Handle network errors or unexpected issues
    console.error("Error during getting classrooms:", error);
    return { ok: false, error: error };
  }
};

/**
 * Get classroom dashboard metrics
 * @param {string} classroomId - ID of the classroom
 * @returns {Promise} - Promise resolving to dashboard data
 */
export const getClassroomDashboardMetrics = async (classroomId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/classrooms/dashboard/${classroomId}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData?.error || `Request failed with status ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching dashboard metrics:", error);
    throw error;
  }
};

/**
 * Get classroom attendance data
 * @param {string} classroomId - ID of the classroom
 * @param {string} timeRange - Time range filter (weekly, monthly, yearly)
 * @returns {Promise} - Promise resolving to attendance data
 */
export const getClassroomAttendanceData = async (
  classroomId,
  timeRange = "weekly"
) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/classrooms/attendance/${classroomId}?timeRange=${timeRange}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData?.error || `Request failed with status ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching attendance data:", error);
    throw error;
  }
};

/**
 * Get classroom most practiced topics
 * @param {string} classroomId - ID of the classroom
 * @returns {Promise} - Promise resolving to top topics data
 */
export const getTopPracticedTopics = async (classroomId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/classrooms/topics/${classroomId}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData?.error || `Request failed with status ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching top practiced topics:", error);
    throw error;
  }
};

/**
 * Get classroom most wronged questions
 * @param {string} classroomId - ID of the classroom
 * @returns {Promise} - Promise resolving to most wronged questions data
 */
export const getMostWrongedQuestions = async ({
  classroomId,
  pageNumber = 0,
  pageSize = 10,
  text = "",
  subjectId = "",
  topicId = "",
}) => {
  try {
    let url = `/classrooms/wrong_questions/${classroomId}?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (text) {
      url += `&text=${text}`;
    }
    if (subjectId) {
      url += `&subjectId=${subjectId}`;
    }
    if (topicId) {
      url += `&topicId=${topicId}`;
    }
    const response = await makeAuthenticatedRequest(url);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData?.error || `Request failed with status ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching most wronged questions:", error);
    throw error;
  }
};

/**
 * Get classroom most wronged questions
 * @param {string} classroomId - ID of the classroom
 * @returns {Promise} - Promise resolving to most wronged questions data
 */
export const getHardestTopicsData = async ({
  classroomId,
  pageNumber,
  pageSize,
}) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/classrooms/topics/hardest/${classroomId}?pageNumber=${pageNumber}&pageSize=${pageSize}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData?.error || `Request failed with status ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching most wronged questions:", error);
    throw error;
  }
};

/**
 * Get leaderboard data for a classroom
 * @param {string} classroomId - ID of the classroom
 * @returns {Promise} - Promise resolving to leaderboard data
 */
export const getLeaderboardData = async (classroomId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/classrooms/leaderboard/${classroomId}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData?.error || `Request failed with status ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching leaderboard data:", error);
    throw error;
  }
};

/**
 * Get attendance danger zone data
 * @param {string} classroomId - ID of the classroom
 * @returns {Promise} - Promise resolving to at-risk students data
 */
export const getAttendanceDangerZoneData = async (classroomId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/classrooms/attendance/${classroomId}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData?.error || `Request failed with status ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching attendance danger zone data:", error);
    throw error;
  }
};

/**
 * Test function to directly call the dashboard endpoint with a specific classroom ID
 * @returns {Promise} - Promise resolving to test data
 */
export const testDashboardEndpoint = async () => {
  try {
    const classroomId = "66bcee27da08e28bacafecd4"; // Hardcoded ID for testing
    const response = await makeAuthenticatedRequest(
      `/classrooms/dashboard/${classroomId}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Error response:", errorData);
      throw new Error(
        errorData?.error || `Request failed with status ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("TEST - Error testing dashboard endpoint:", error);
    throw error;
  }
};

/**
 * Direct function to call the dashboard endpoint with exact path from user request
 * @param {string} classroomId - ID of the classroom
 * @returns {Promise} - Promise resolving to dashboard data
 */
export const getDirectDashboardData = async (
  classroomId = "66bcee27da08e28bacafecd4"
) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/classrooms/dashboard/${classroomId}`
    );

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching direct dashboard data:", error);
    throw error;
  }
};

export const getTopicsMostPracticedData = async ({
  classroomId,
  pageSize = 5,
  pageNumber = 0,
  accuracyType,
  topicName,
  subjectId,
}) => {
  try {
    let url = `/classrooms/topics/most_practiced/${classroomId}?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (accuracyType) {
      url += `&accuracyType=${accuracyType}`;
    }
    if (topicName) {
      url += `&topicName=${topicName}`;
    }
    if (subjectId) {
      url += `&subjectId=${subjectId}`;
    }
    const response = await makeAuthenticatedRequest(url);

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching direct dashboard data:", error);
    throw error;
  }
};

/**
 * Get student leaderboard data for a classroom
 * @param {string} classroomId - ID of the classroom
 * @returns {Promise} - Promise resolving to leaderboard data
 */
export const getStudentLeaderboardData = async (classroomId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/classrooms/student_leaderboard/${classroomId}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData?.error || `Request failed with status ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching student leaderboard data:", error);
    throw error;
  }
};
